import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { lazy, Suspense } from 'react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { IntroGate } from '@/components/intro/IntroSplash';
import { MotionBurstProvider } from '@/components/motion/MotionBurstContext';
import { RouteSkeleton } from '@/components/motion/RouteSkeleton';
import { LiveStatusProvider } from '@/lib/live-status';
import App from './App';
import './i18n';
import './index.css';
import { initAnalytics } from './lib/analytics';
import { queryClient, shouldPersistQueryKey } from './lib/query';
import { AuthProvider } from './lib/auth';

initAnalytics();

const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'oche-query-cache',
});

const Overview = lazy(() => import('./routes/Overview').then((m) => ({ default: m.Overview })));
const History = lazy(() => import('./routes/History').then((m) => ({ default: m.History })));
const CreateSession = lazy(() =>
  import('./routes/CreateSession').then((m) => ({ default: m.CreateSession })),
);
const SessionDetail = lazy(() =>
  import('./routes/SessionDetail').then((m) => ({ default: m.SessionDetail })),
);
const ApiKeys = lazy(() => import('./routes/ApiKeys').then((m) => ({ default: m.ApiKeys })));

function Page({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<RouteSkeleton />}>{children}</Suspense>;
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      {
        index: true,
        element: (
          <Page>
            <Overview />
          </Page>
        ),
      },
      {
        path: 'history',
        element: (
          <Page>
            <History />
          </Page>
        ),
      },
      {
        path: 'sessions/new',
        element: (
          <Page>
            <CreateSession />
          </Page>
        ),
      },
      {
        path: 'history/:id',
        element: (
          <Page>
            <SessionDetail />
          </Page>
        ),
      },
      {
        path: 'settings/keys',
        element: (
          <Page>
            <ApiKeys />
          </Page>
        ),
      },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24,
        dehydrateOptions: {
          shouldDehydrateQuery: (q) => shouldPersistQueryKey(q.queryKey),
        },
      }}
    >
      <AuthProvider>
        <LiveStatusProvider>
          <MotionBurstProvider>
            <IntroGate>
              <RouterProvider router={router} />
            </IntroGate>
          </MotionBurstProvider>
        </LiveStatusProvider>
      </AuthProvider>
    </PersistQueryClientProvider>
  </StrictMode>,
);
